import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../services/api';
import { useTranslation } from 'react-i18next';
import './Approvals.css';

const TABS = ['Pending', 'Approved', 'Rejected', 'All'];

/**
 * Approvals — Noris-team only.
 *
 * Lists fuel-station registration requests across all clients. Pending
 * requests can be Approved or Rejected. The endpoint enforces noris-team
 * access on the backend; this UI is also hidden from non-team users by the
 * Sidebar (defence in depth).
 */
export default function Approvals() {
  const { isNorisTeam } = useAuth();
  const { t: tr } = useTranslation();
  const [tab, setTab]       = useState('Pending');
  const [rows, setRows]     = useState([]);
  const [loading, setL]     = useState(false);
  const [error, setError]   = useState('');
  const [confirm, setConfirm] = useState(null);   // { kind:'approve'|'reject', station }
  const [note, setNote]     = useState('');
  const [busy, setBusy]     = useState(false);
  const [toast, setToast]   = useState('');

  const flash = (m) => { setToast(m); setTimeout(() => setToast(''), 3500); };

  const load = useCallback(async () => {
    setL(true); setError('');
    try {
      const data = tab === 'Pending'
        ? await api.stations.pending()
        : await api.stations.all(tab === 'All' ? '' : tab);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || tr('approvals.errors.couldNotLoad'));
    }
    setL(false);
  }, [tab]);

  useEffect(() => { if (isNorisTeam) load(); }, [load, isNorisTeam]);

  const onApprove = (s) => { setConfirm({ kind: 'approve', station: s }); setNote(''); };
  const onReject  = (s) => { setConfirm({ kind: 'reject',  station: s }); setNote(''); };

  const performAction = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      if (confirm.kind === 'approve') {
        await api.stations.approve(confirm.station.id);
        flash(tr('approvals.toasts.approved', { name: confirm.station.name }));
      } else {
        await api.stations.reject(confirm.station.id, note);
        flash(tr('approvals.toasts.rejected', { name: confirm.station.name }));
      }
      setConfirm(null);
      setNote('');
      await load();
    } catch (e) {
      setError(e.message || tr('approvals.errors.actionFailed'));
    }
    setBusy(false);
  };

  /* ── Hidden if not Noris team ── */
  if (!isNorisTeam) {
    return (
      <div className="page">
        <div className="page-header">
          <h2 className="page-title">{tr('approvals.title')}</h2>
        </div>
        <div className="ap-empty">
          <span className="ap-empty-icon">🔒</span>
          <p>{tr('approvals.restrictedNoris')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ap-page">
      <div className="page-header">
        <h2 className="page-title">{tr('approvals.titleFull')}</h2>
      </div>

      <div className="ap-intro">
        {tr('approvals.intro')}
      </div>

      {toast && <div className="ap-toast">{toast}</div>}
      {error && <div className="ap-error">⚠️ {error}</div>}

      {/* Tabs */}
      <div className="ap-tabs">
        {TABS.map(t => (
          <button key={t}
                  className={`ap-tab ${tab === t ? 'ap-tab-active' : ''}`}
                  onClick={() => setTab(t)}>
            {tr('common.' + t.toLowerCase())}
          </button>
        ))}
        <button className="ap-refresh" onClick={load} disabled={loading} title={tr('approvals.reload')}>
          {loading ? '⏳' : '↻'}
        </button>
      </div>

      {/* List */}
      {loading && rows.length === 0 ? (
        <div className="ap-empty">{tr('common.loading')}</div>
      ) : rows.length === 0 ? (
        <div className="ap-empty">
          <span className="ap-empty-icon">📭</span>
          <p>{tr('approvals.noStations', { status: tab === 'All' ? '' : tab.toLowerCase() })}</p>
        </div>
      ) : (
        <div className="ap-table-wrap">
          <table className="ap-table">
            <thead>
              <tr>
                <th>{tr('common.station')}</th>
                <th>{tr('approvals.client')}</th>
                <th>{tr('approvals.stateGstin')}</th>
                <th>{tr('approvals.requested')}</th>
                <th>{tr('common.status')}</th>
                <th>{tr('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(s => (
                <tr key={s.id}>
                  <td>
                    <div className="ap-cell-strong">{s.name}</div>
                    {s.address && <div className="ap-cell-sub">{s.address}</div>}
                    {s.phone   && <div className="ap-cell-sub">📞 {s.phone}</div>}
                  </td>
                  <td>
                    <div className="ap-cell-strong">{s.client_name || '—'}</div>
                    <div className="ap-cell-sub">{s.client_email || ''}</div>
                  </td>
                  <td>
                    <div>{s.state || '—'}</div>
                    {s.gstin && <div className="ap-cell-sub ap-mono">{s.gstin}</div>}
                  </td>
                  <td className="ap-cell-sub">
                    {(s.requested_at || '').replace('T', ' ').slice(0, 16)}
                  </td>
                  <td>
                    <span className={`ap-status ap-status-${(s.status || '').toLowerCase()}`}>
                      {s.status}
                    </span>
                    {s.decision_note && (
                      <div className="ap-cell-sub" title={s.decision_note}>
                        {tr('approvals.note')} {s.decision_note}
                      </div>
                    )}
                  </td>
                  <td>
                    {s.status === 'Pending' ? (
                      <div className="ap-actions">
                        <button className="btn btn-success btn-sm" onClick={() => onApprove(s)}>
                          ✓ {tr('approvals.approve')}
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={() => onReject(s)}>
                          ✕ {tr('approvals.reject')}
                        </button>
                      </div>
                    ) : (
                      <span className="ap-cell-sub">
                        {s.decided_by ? tr('approvals.by', { name: s.decided_by }) : '—'}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Confirmation modal */}
      {confirm && (
        <div className="ap-modal-backdrop" onClick={() => !busy && setConfirm(null)}>
          <div className="ap-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ap-modal-title">
              {confirm.kind === 'approve' ? tr('approvals.approveStation') : tr('approvals.rejectStation')}
            </h3>
            <div className="ap-modal-body">
              <div className="ap-cell-strong">{confirm.station.name}</div>
              <div className="ap-cell-sub">
                {tr('approvals.client')}: {confirm.station.client_name} ({confirm.station.client_email})
              </div>

              {confirm.kind === 'reject' && (
                <div style={{ marginTop: 12 }}>
                  <label className="ap-label">{tr('approvals.reasonOptional')}</label>
                  <textarea
                    className="ap-textarea"
                    rows={3}
                    placeholder={tr('approvals.reasonPlaceholder')}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </div>
              )}
            </div>

            <div className="ap-modal-actions">
              <button className="btn btn-light" onClick={() => setConfirm(null)} disabled={busy}>
                {tr('common.cancel')}
              </button>
              <button
                className={`btn ${confirm.kind === 'approve' ? 'btn-success' : 'btn-danger'}`}
                onClick={performAction}
                disabled={busy}
              >
                {busy ? '⏳ ' + tr('approvals.working') : (confirm.kind === 'approve' ? '✓ ' + tr('approvals.approve') : '✕ ' + tr('approvals.reject'))}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
